import type { LatLngExpression } from "leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { type FC, useCallback, useEffect, useMemo, useRef } from "react";
import {
	Circle,
	MapContainer,
	Marker,
	TileLayer,
	useMap,
	useMapEvents,
} from "react-leaflet";
import {
	MetaAdsDistanceUnit,
	type MetaAdsGeoExclusions,
	MetaAdsGeoTargetingMode,
	type MetaAdsLocationPin,
} from "shared";

// Fix Leaflet default icon issue with bundlers
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)
	._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl:
		"https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
	iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
	shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const PIN_COLORS = [
	"#4285F4",
	"#EA4335",
	"#34A853",
	"#FBBC05",
	"#9C27B0",
	"#FF6D00",
	"#00BCD4",
	"#E91E63",
	"#3F51B5",
	"#795548",
];

export function getPinColor(index: number): string {
	return PIN_COLORS[index % PIN_COLORS.length];
}

function radiusToMeters(radius: number, unit: MetaAdsDistanceUnit): number {
	return unit === MetaAdsDistanceUnit.MILE ? radius * 1609.34 : radius * 1000;
}

function createColoredIcon(color: string, size = 24) {
	return L.divIcon({
		className: "",
		html: `<div style="
			width: ${size}px; height: ${size}px;
			background: ${color};
			border: 2px solid white;
			border-radius: 50%;
			box-shadow: 0 2px 4px rgba(0,0,0,0.3);
		"></div>`,
		iconSize: [size, size],
		iconAnchor: [size / 2, size / 2],
	});
}

const EXCLUSION_COLOR = "#dc3545";
const exclusionIcon = createColoredIcon(EXCLUSION_COLOR, 20);

interface Props {
	pins: MetaAdsLocationPin[];
	canAddPin: boolean;
	onAddPin: (lat: number, lng: number) => void;
	onPinDrag: (pinId: string, lat: number, lng: number) => void;
	mode?: MetaAdsGeoTargetingMode;
	exclusions?: MetaAdsGeoExclusions;
	onAddExclusionPin?: (lat: number, lng: number) => void;
}

const FitBounds: FC<{
	pins: MetaAdsLocationPin[];
	exclusionPins?: MetaAdsLocationPin[];
}> = ({ pins, exclusionPins }) => {
	const map = useMap();
	const prevCount = useRef(0);

	const allPoints = useMemo(() => {
		const points: LatLngExpression[] = pins.map(
			(p) => [p.latitude, p.longitude] as LatLngExpression,
		);
		if (exclusionPins) {
			for (const ep of exclusionPins) {
				points.push([ep.latitude, ep.longitude] as LatLngExpression);
			}
		}
		return points;
	}, [pins, exclusionPins]);

	useEffect(() => {
		if (allPoints.length === 0) return;
		if (allPoints.length !== prevCount.current) {
			const bounds = L.latLngBounds(allPoints);
			map.fitBounds(bounds.pad(0.3));
		}
		prevCount.current = allPoints.length;
	}, [allPoints, map]);

	return null;
};

const ClickHandler: FC<{
	canAddPin: boolean;
	onAddPin: (lat: number, lng: number) => void;
}> = ({ canAddPin, onAddPin }) => {
	useMapEvents({
		click(e) {
			if (canAddPin) {
				onAddPin(e.latlng.lat, e.latlng.lng);
			}
		},
	});
	return null;
};

export const GeoTargetingMap: FC<Props> = ({
	pins,
	canAddPin,
	onAddPin,
	onPinDrag,
	mode = MetaAdsGeoTargetingMode.RADIUS_PIN,
	exclusions,
	onAddExclusionPin,
}) => {
	const defaultCenter: LatLngExpression = [39.8283, -98.5795]; // Center of US
	const mapRef = useRef<L.Map>(null);

	const handleResetView = useCallback(() => {
		const map = mapRef.current;
		if (!map) return;
		if (pins.length === 0) {
			map.setView(defaultCenter, 4);
			return;
		}
		const bounds = L.latLngBounds(
			pins.map((p) => [p.latitude, p.longitude] as LatLngExpression),
		);
		map.fitBounds(bounds.pad(0.3));
	}, [pins]);

	const icons = useMemo(
		() => pins.map((_, i) => createColoredIcon(getPinColor(i))),
		[pins],
	);

	const exclusionCustomLocations = exclusions?.customLocations ?? [];

	const handleMapClick = useCallback(
		(lat: number, lng: number) => {
			// If in radius_pin mode, add inclusion pin. Otherwise, add exclusion pin.
			if (mode === MetaAdsGeoTargetingMode.RADIUS_PIN && canAddPin) {
				onAddPin(lat, lng);
			} else if (onAddExclusionPin) {
				onAddExclusionPin(lat, lng);
			}
		},
		[mode, canAddPin, onAddPin, onAddExclusionPin],
	);

	const allowClick =
		(mode === MetaAdsGeoTargetingMode.RADIUS_PIN && canAddPin) ||
		!!onAddExclusionPin;

	return (
		<div style={{ position: "relative", height: "100%", minHeight: 400 }}>
			<MapContainer
				key="campaign-wizard-geo-map"
				center={defaultCenter}
				zoom={4}
				scrollWheelZoom
				style={{ height: "100%", width: "100%", borderRadius: 8 }}
				ref={mapRef}
			>
				<TileLayer
					attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
				/>
				<ClickHandler canAddPin={allowClick} onAddPin={handleMapClick} />
				<FitBounds pins={pins} exclusionPins={exclusionCustomLocations} />

				{/* Inclusion pins (radius mode) */}
				{mode === MetaAdsGeoTargetingMode.RADIUS_PIN &&
					pins.map((pin, index) => (
						<PinMarker
							key={pin.id}
							pin={pin}
							icon={icons[index]}
							color={getPinColor(index)}
							onDrag={onPinDrag}
						/>
					))}

				{/* Exclusion radius pins (any mode) */}
				{exclusionCustomLocations.map((pin) => (
					<ExclusionPinMarker key={pin.id} pin={pin} />
				))}
			</MapContainer>

			{/* Reset view button */}
			<button
				type="button"
				className="btn btn-sm btn-light"
				style={{
					position: "absolute",
					top: 10,
					right: 10,
					zIndex: 1000,
					boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
				}}
				onClick={handleResetView}
			>
				Reset View
			</button>

			{/* Legend */}
			<div
				style={{
					position: "absolute",
					bottom: 10,
					left: 10,
					zIndex: 1000,
					background: "rgba(255,255,255,0.9)",
					borderRadius: 6,
					padding: "6px 10px",
					fontSize: 11,
					boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
					display: "flex",
					flexDirection: "column",
					gap: 3,
				}}
			>
				{mode === MetaAdsGeoTargetingMode.RADIUS_PIN && (
					<LegendItem color="#4285F4" label="Inclusion pin" />
				)}
				{exclusionCustomLocations.length > 0 && (
					<LegendItem color={EXCLUSION_COLOR} label="Exclusion zone" />
				)}
				{mode !== MetaAdsGeoTargetingMode.RADIUS_PIN && (
					<span className="text-muted">Click map to add exclusion pin</span>
				)}
			</div>
		</div>
	);
};

const LegendItem: FC<{ color: string; label: string }> = ({ color, label }) => (
	<div style={{ display: "flex", alignItems: "center", gap: 5 }}>
		<div
			style={{
				width: 10,
				height: 10,
				borderRadius: "50%",
				background: color,
				border: "1px solid #ccc",
			}}
		/>
		<span>{label}</span>
	</div>
);

const PinMarker: FC<{
	pin: MetaAdsLocationPin;
	icon: L.DivIcon;
	color: string;
	onDrag: (pinId: string, lat: number, lng: number) => void;
}> = ({ pin, icon, color, onDrag }) => {
	const position: LatLngExpression = [pin.latitude, pin.longitude];
	const radiusMeters = radiusToMeters(pin.radius, pin.distanceUnit);

	return (
		<>
			<Circle
				center={position}
				radius={radiusMeters}
				pathOptions={{
					color,
					fillColor: color,
					fillOpacity: 0.15,
					weight: 2,
				}}
			/>
			<Marker
				position={position}
				icon={icon}
				draggable
				eventHandlers={{
					dragend(e) {
						const marker = e.target;
						const pos = marker.getLatLng();
						onDrag(pin.id, pos.lat, pos.lng);
					},
				}}
			/>
		</>
	);
};

const ExclusionPinMarker: FC<{ pin: MetaAdsLocationPin }> = ({ pin }) => {
	const position: LatLngExpression = [pin.latitude, pin.longitude];
	const radiusMeters = radiusToMeters(pin.radius, pin.distanceUnit);

	return (
		<>
			<Circle
				center={position}
				radius={radiusMeters}
				pathOptions={{
					color: EXCLUSION_COLOR,
					fillColor: EXCLUSION_COLOR,
					fillOpacity: 0.2,
					weight: 2,
					dashArray: "6 4",
				}}
			/>
			<Marker position={position} icon={exclusionIcon} />
		</>
	);
};
